import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from '@mottu-ops/keycloak-angular';
import { AuthService } from '@mottu-ops/sso';
import { BehaviorSubject, timer } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { MonitorRolesEnum } from 'src/app/components/auth/utils/monitor-roles.enum';
import { MonitorService } from 'src/app/shared/services/monitor.service';
import { SignalRService } from 'src/app/shared/services/signalR.service';
import { environment } from '../../../environments/environment';
import { AuditUserModel } from '../models/audit-user.model';
import { BaseRequestResult } from '../models/BaseRequestResult.model';
import { CurrentUser } from '../models/CurrentUser.model';
import {
  UsuarioSuporte,
  UsuarioSuporteModel,
} from '../models/PwaSuporte.model';
import { UserSuporte } from '../models/ServicoVeiculoSuporte.model';
import { UsuarioDataModel } from '../models/Usuario.model';
import { OneSignalService } from './oneSignal.service';
import { SnackbarService } from './snackBar.service';
import { UserAgentService } from './user-agent.service';

@Injectable()
export class InternalAuthService {
  public currentUserSubject = new BehaviorSubject<boolean>(false);
  public currentUser;
  private baseUrlMonitor = environment.baseUrlMonitor;
  storages = [
    'isSupportUser',
    'boraboraUser',
    'currentUser',
    'isRenavamUser',
    'isFabricaUser',
    'isGuinchoUser',
    'NotificacoesSuporteRua',
    'NotificacoesRecolhimento',
    'boardParam',
    'mottuFieldApp',
  ];

  get userLogged(): BaseRequestResult<CurrentUser<UsuarioSuporte>> {
    return JSON.parse(localStorage.getItem('currentUser'));
  }

  get isGuinchoUser() {
    if (!this.userLogged) {
      return false;
    }

    return this.checkUserRoles([MonitorRolesEnum.FLEET_THIRD_ADMIN]);
  }

  get usuario(): string {
    const usuario = this.userLogged.dataResult?.data?.nome;
    if (usuario) {
      return usuario;
    }
    return '';
  }

  get currentUserLogged(): UsuarioDataModel {
    if (this.userLogged && this.userLogged?.dataResult) {
      const user = this.userLogged?.dataResult;

      if (user && user?.data) {
        return user?.data;
      }
    }

    return null;
  }

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private snackBarService: SnackbarService,
    private signalRService: SignalRService,
    private monitorService: MonitorService,
    private oneSignalService: OneSignalService,
    private keycloak: KeycloakService,
    private userAgentService: UserAgentService,
    private authService: AuthService
  ) {
  }

  login(user: any) {
    return this.http
      .post(this.baseUrlMonitor + 'api/v1/IAM/authentication', user)
      .pipe(
        map((response: any) => {
          this.clearLocalStorage();
          if (response.dataResult) {
            this.setPlayerId(response.dataResult.data.id.toString());
            localStorage.setItem('currentUser', JSON.stringify(response));
            this.setRouteToRedirect(response.dataResult.data);
          }
          return response;
        })
      );
  }

  async authorize() {
    return this.http
      .post(this.baseUrlMonitor + 'api/v1/IAM/user', {
        token: this.authService.credentials.accessToken,
      })
      .toPromise()
      .then((response: any) => {
        this.clearLocalStorage();
        if (response.dataResult) {
          this.setPlayerId(response.dataResult.data.id.toString());
          localStorage.setItem('currentUser', JSON.stringify(response));
        } else if (response.erro) {
          this.keycloak.logout(window.location.origin);
        }
      })
      .catch(() => {
        this.keycloak.logout(window.location.origin);
      });
  }

  setPlayerId(id) {
    try {
      const playerId = localStorage.getItem('playerId');

      if (playerId && playerId != 'undefined') {
        this.oneSignalService.setExternalUserId(playerId, id).subscribe();
        localStorage.setItem('playerId', playerId);
      }
    } catch (error) {
    }
  }

  async redirect() {
    if (this.userLogged) {
      await this.setRouteToRedirect(this.userLogged.dataResult.data);
      const playerId = document.location.hash.split('=')[1];
      localStorage.setItem('playerId', playerId);
    }
  }

  async setRouteToRedirect(usuarioLogado: UsuarioSuporteModel) {
    if (this.keycloak.isLoggedIn()) {
      const pitStopUser = this.authService.validateRoles([
        MonitorRolesEnum.FLEET_PITSTOP,
      ]);
      const adminUser = this.authService.validateRoles([
        MonitorRolesEnum.FLEET_ADMIN,
      ]);

      if (pitStopUser && !adminUser) {
        return this.redirectLogin(['/home/pitstop']);
      }

      if (adminUser) {
        return this.redirectLogin(['/home/servicos-template/leaflet']);
      }

      if (
        this.checkUserRoles([MonitorRolesEnum.RENTAL_FLEET_MOTTUFIELD_SALES])
      ) {
        return this.redirectLogin(['/home/inventario-transferencia']);
      }

      return this.redirectLogin(['/home/servicos-template/leaflet']);
    }
  }

  redirectLogin(page) {
    const returnUrl = this.route.snapshot.queryParams.returnUrl;

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl).then();
      return;
    }

    this.router.navigate(page).then();
  }

  async logout() {
    await this.logoutAdmin();
  }

  async logoutAdmin() {
    this.clearLocalStorage();
    this.signalRService.disconnectSignalR();
    this.currentUserSubject.next(false);
    await this.keycloak.logout(this.document.location.origin);
  }

  logoutField() {
    try {
      const playerId = localStorage.getItem('playerId');
      if (playerId && playerId != 'undefined') {
        this.oneSignalService.setExternalUserId(playerId, '').subscribe();
      }
    } catch (error) {
    }
  }

  clearLocalStorage() {
    this.storages.forEach((storage) => {
      localStorage.removeItem(storage);
    });
  }

  checkUserRoles(roles: string[]) {
    return this.authService.validateRoles(roles, 'or');
  }

  checkUserRoles$(roles: string[]) {
    return timer(100, 500).pipe(
      map(() => {
        if (this.authService?.roles !== undefined) {
          return this.authService.validateRoles(roles, 'or');
        }

        return undefined;
      }),
      filter((value) => value !== undefined),
      first()
    );
  }

  desvincularUsuarioPwa() {
    this.monitorService
      .desvincularUserVeiculo(this.userLogged.dataResult.data.id)
      .subscribe((response: BaseRequestResult<UserSuporte>) => {
        if (!response.erro) {
          this.logoutField();
          this.logoutAdmin();
        } else {
          this.snackBarService.showSnackBar(response.mensagemErro, 'error');
        }
      }),
      (error) => {
        this.snackBarService.showSnackBar(error.error.mensagemErro, 'error');
      };
  }

  sendAuditData(data: AuditUserModel) {
    return this.http.post(this.baseUrlMonitor + 'api/v1/IAM/audit-user', data);
  }
}
